import { useEffect, useState, type FormEvent } from "react";

import Modal from "../../components/Modal";
import { ApiError } from "../../api/client";
import { useDeleteEntry, useUpdateEntry } from "../../api/mutations";
import { useEntry } from "../../api/queries";
import type { CarSummary, FormOptionsResponse, UpdateEntryInput } from "../../api/types";
import { FUEL_TYPES_BY_CATEGORY, fuelTypeLabel } from "../garage/carFormConstants";

interface EntryDetailModalProps {
  entryId: number | null;
  onClose: () => void;
  cars: CarSummary[];
  formOptions: FormOptionsResponse;
}

export default function EntryDetailModal({
  entryId,
  onClose,
  cars,
  formOptions,
}: EntryDetailModalProps) {
  const isOpen = entryId !== null;
  const entryQuery = useEntry(entryId);
  const update = useUpdateEntry(entryId ?? 0);
  const del = useDeleteEntry();

  const entry = entryQuery.data;
  const car = entry ? cars.find((c) => c.id === entry.car_id) : undefined;
  const fuelTypes = car ? FUEL_TYPES_BY_CATEGORY[car.fuel_category] : [];

  const [stationName, setStationName] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [entryDate, setEntryDate] = useState("");
  const [liters, setLiters] = useState("");
  const [amountEur, setAmountEur] = useState("");
  const [fuelType, setFuelType] = useState("");
  const [isFullTank, setIsFullTank] = useState<"true" | "false" | "unknown">("unknown");
  const [odometerKm, setOdometerKm] = useState("");
  const [notes, setNotes] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Track which entry we've seeded the form with, to avoid resetting mid-edit
  // when TanStack Query refetches in the background.
  const [seededId, setSeededId] = useState<number | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setSeededId(null);
      return;
    }
    if (!entry || entry.id === seededId) return;
    setStationName(entry.station_name);
    setCity(entry.city);
    setCountry(entry.country);
    setEntryDate(entry.datetime.slice(0, 16));
    setLiters(entry.liters.toString());
    setAmountEur(entry.amount_eur.toString());
    setFuelType(entry.fuel_type);
    setIsFullTank(entry.is_full_tank);
    setOdometerKm(entry.odometer_km !== null ? String(entry.odometer_km) : "");
    setNotes(entry.notes);
    setFieldErrors({});
    setSubmitError(null);
    setSeededId(entry.id);
  }, [isOpen, entry, seededId]);

  const derivedPricePerLiter =
    liters && amountEur && parseFloat(liters) > 0
      ? (parseFloat(amountEur) / parseFloat(liters)).toFixed(3)
      : "—";

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!entry) return;
    setFieldErrors({});
    setSubmitError(null);

    const payload: UpdateEntryInput = {
      station_name: stationName.trim(),
      city: city.trim(),
      country,
      entry_date: entryDate,
      liters: parseFloat(liters),
      amount_eur: parseFloat(amountEur),
      fuel_type: fuelType,
      is_full_tank: isFullTank,
      notes,
    };
    if (odometerKm && entry.odometer_entry_id !== null) {
      payload.odometer_km = parseFloat(odometerKm);
    }

    try {
      await update.mutateAsync(payload);
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.errors) {
        setFieldErrors(err.errors);
      } else {
        setSubmitError(err instanceof ApiError ? err.message : "Couldn't save changes.");
      }
    }
  }

  async function handleDelete() {
    if (!entry) return;
    if (!window.confirm("Delete this entry? This cannot be undone.")) return;
    setSubmitError(null);
    try {
      await del.mutateAsync(entry.id);
      onClose();
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.message : "Couldn't delete entry.");
    }
  }

  const busy = update.isPending || del.isPending;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Entry details">
      {isOpen && entryQuery.isLoading && (
        <p className="empty-state" style={{ padding: "24px 0" }}>Loading…</p>
      )}
      {isOpen && entryQuery.isError && (
        <p className="form-status" data-variant="error" role="alert">
          Could not load entry.
        </p>
      )}
      {isOpen && entry && (
        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          <div className="field">
            <label className="field__label" htmlFor="ed-station">
              Station
            </label>
            <input
              id="ed-station"
              className="field__input"
              type="text"
              maxLength={255}
              value={stationName}
              onChange={(e) => setStationName(e.target.value)}
            />
            {fieldErrors.station_name && (
              <p className="field__error">{fieldErrors.station_name}</p>
            )}
          </div>

          <div className="field-row">
            <div className="field field--grow">
              <label className="field__label" htmlFor="ed-city">
                City
              </label>
              <input
                id="ed-city"
                className="field__input"
                type="text"
                maxLength={255}
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
              {fieldErrors.city && (
                <p className="field__error">{fieldErrors.city}</p>
              )}
            </div>
            <div className="field field--country">
              <label className="field__label" htmlFor="ed-country">
                Country
              </label>
              <select
                id="ed-country"
                className="field__input field__select"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
              >
                {formOptions.countries.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code} — {c.name}
                  </option>
                ))}
              </select>
              {fieldErrors.country && (
                <p className="field__error">{fieldErrors.country}</p>
              )}
            </div>
          </div>

          <div className="field-row">
            <div className="field field--grow">
              <label className="field__label" htmlFor="ed-date">
                Date & time
              </label>
              <input
                id="ed-date"
                className="field__input"
                type="datetime-local"
                value={entryDate}
                onChange={(e) => setEntryDate(e.target.value)}
              />
              {fieldErrors.entry_date && (
                <p className="field__error">{fieldErrors.entry_date}</p>
              )}
            </div>
            <div className="field field--grow">
              <label className="field__label" htmlFor="ed-fuel-type">
                Fuel type
              </label>
              <select
                id="ed-fuel-type"
                className="field__input field__select"
                value={fuelType}
                onChange={(e) => setFuelType(e.target.value)}
              >
                {fuelTypes.map((ft) => (
                  <option key={ft} value={ft}>
                    {fuelTypeLabel(ft)}
                  </option>
                ))}
              </select>
              {fieldErrors.fuel_type && (
                <p className="field__error">{fieldErrors.fuel_type}</p>
              )}
            </div>
          </div>

          <div className="field-row field-row--numbers">
            <div className="field">
              <label className="field__label" htmlFor="ed-liters">
                Liters
              </label>
              <input
                id="ed-liters"
                className="field__input"
                type="number"
                min="0.001"
                step="0.001"
                value={liters}
                onChange={(e) => setLiters(e.target.value)}
              />
              {fieldErrors.liters && (
                <p className="field__error">{fieldErrors.liters}</p>
              )}
            </div>
            <div className="field">
              <label className="field__label" htmlFor="ed-amount">
                Total (€)
              </label>
              <input
                id="ed-amount"
                className="field__input"
                type="number"
                min="0.01"
                step="0.01"
                value={amountEur}
                onChange={(e) => setAmountEur(e.target.value)}
              />
              {fieldErrors.amount_eur && (
                <p className="field__error">{fieldErrors.amount_eur}</p>
              )}
            </div>
            <div className="field">
              <label className="field__label">€/L</label>
              <input
                className="field__input"
                type="text"
                readOnly
                value={derivedPricePerLiter}
              />
            </div>
          </div>

          {entry.odometer_entry_id !== null && (
            <div className="field">
              <label className="field__label" htmlFor="ed-odometer">
                Odometer (km)
              </label>
              <input
                id="ed-odometer"
                className="field__input"
                type="number"
                min={entry.prev_odometer_km ?? undefined}
                max={entry.next_odometer_km ?? undefined}
                step="1"
                value={odometerKm}
                onChange={(e) => setOdometerKm(e.target.value)}
              />
              {(entry.prev_odometer_km !== null || entry.next_odometer_km !== null) && (
                <p className="field__error" style={{ color: "var(--ink-muted)" }}>
                  {[
                    entry.prev_odometer_km !== null &&
                      `min ${entry.prev_odometer_km.toLocaleString()} km`,
                    entry.next_odometer_km !== null &&
                      `max ${entry.next_odometer_km.toLocaleString()} km`,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              )}
              {fieldErrors.odometer_km && (
                <p className="field__error">{fieldErrors.odometer_km}</p>
              )}
            </div>
          )}

          <div className="field">
            <label className="field__label" htmlFor="ed-full-tank">
              Full tank
            </label>
            <select
              id="ed-full-tank"
              className="field__input field__select"
              value={isFullTank}
              onChange={(e) =>
                setIsFullTank(e.target.value as "true" | "false" | "unknown")
              }
            >
              <option value="true">Yes</option>
              <option value="false">No</option>
              <option value="unknown">Unknown</option>
            </select>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="ed-notes">
              Notes
            </label>
            <textarea
              id="ed-notes"
              className="field__input field__textarea"
              maxLength={1024}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          {submitError && (
            <p className="form-status" data-variant="error" role="alert">
              {submitError}
            </p>
          )}

          <div className="form-actions" style={{ justifyContent: "space-between" }}>
            <button
              type="button"
              className="button button--danger"
              onClick={handleDelete}
              disabled={busy}
            >
              {del.isPending ? "Deleting…" : "Delete"}
            </button>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                type="button"
                className="button"
                onClick={onClose}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="button button--primary"
                disabled={busy}
              >
                {update.isPending ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </form>
      )}
    </Modal>
  );
}
